import { memo, useContext } from 'react';
import styled from '@emotion/styled';

import { WWContext, type WWContextValue } from '../WWContext';
import Header from './Header';

import { isWwsWeddingWebsiteV2 } from '../../types/graphql';

import { css, type ThemeProps } from '@emotion/react';
import type { CSSObject } from '@emotion/styled';
import DynamicNextImage from '../NextImage/DynamicNextImage';
import FixedNextImage from '../NextImage/FixedNextImage';
import InlineEditHandler from '../InlineEdit/InlineEditHandler/InlineEditHandler';
import { buildEditCoverPhotoContextMenuButtons } from '../InlineEdit/InlineEditMessageFactory';
import { variables } from '@xo-union/tk-ui-breakpoints';

const HeroHeader = styled(Header)`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: fit-content;
`;

const Overlay = styled('div')`
  display: block;
  position: absolute;
  width: 100%;
  height: 100%;
  background: black;
  opacity: 0.2;
  top: 0;
`;

export const HeroPicture = css`
  .verticalLayout.homePage & {
    @media (${variables.inMobile}) {
      max-height: 50vh;
    }
  }
`;

const DynamicHeroPicture = styled(DynamicNextImage)`
  ${HeroPicture}
`;
const FixedHeroPicture = styled(FixedNextImage)`
  ${HeroPicture}
`;

const HeroWrap = styled('div')`
  width: 100%;
  position: relative;
  margin: 0 auto 30px;

  .verticalLayout.homePage & {
    @media (min-width: 768px) {
      position: sticky;
      top: 1rem;
      padding-top: 20px;
    }
  }

  ${(props: ThemeProps): CSSObject | undefined => props.theme.components.Hero}
`;

interface HeroImageProps {
  id?: string;
  src: string;
  headerOnHero: boolean;
  landscapeCrop: string | undefined | null;
  className?: string | undefined;
  'data-testid'?: string | undefined;
  sizes?: string | undefined;
}

function HeroImage({
  className,
  src,
  headerOnHero,
  landscapeCrop = '',
  sizes,
}: HeroImageProps) {
  const { data } = useContext<WWContextValue>(WWContext);

  if (!isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)) {
    return null;
  }

  const alt =
    data?.weddingWebsiteResult.firstName &&
    data.weddingWebsiteResult.fianceFirstName
      ? `${data.weddingWebsiteResult.firstName} & ${data.weddingWebsiteResult.fianceFirstName} Home Page Banner`
      : 'Home Page Banner';

  const coverPhoto = data?.weddingWebsiteResult.coverPhoto;
  const coverPhotoHeight = coverPhoto?.height;
  const coverPhotoWidth = coverPhoto?.width;
  const parsedRotation = Number(coverPhoto?.rotationAngle);
  const coverPhotoRotation = Number.isFinite(parsedRotation)
    ? parsedRotation
    : 0;

  return (
    <HeroWrap className={className}>
      {headerOnHero ? <HeroHeader heroVersion /> : null}
      <InlineEditHandler
        contextMenuButtons={buildEditCoverPhotoContextMenuButtons()}
        outlineOffset={-2}
      >
        {coverPhotoHeight && coverPhotoWidth ? (
          <FixedHeroPicture
            src={src}
            crop={landscapeCrop || ''}
            alt={alt}
            loading="eager"
            priority
            quality={90}
            width={coverPhotoWidth}
            height={coverPhotoHeight}
            rotation={coverPhotoRotation}
            dataTestId="cover-photo-fixed"
            sizes={sizes}
          />
        ) : (
          <DynamicHeroPicture
            src={src}
            crop={landscapeCrop || ''}
            alt={alt}
            loading="eager"
            priority
            quality={90}
            rotation={coverPhotoRotation}
            dataTestId="cover-photo-dynamic"
            sizes={sizes}
          />
        )}
      </InlineEditHandler>
      {headerOnHero ? <Overlay /> : null}
    </HeroWrap>
  );
}

export default memo(HeroImage);
